import { createRFQTOTAL } from "actions/supplierflow/supplierRfqActions";
import { getCookie, setCookie } from "utils/cookiesHelper";
import { logErrorIfDebug } from "utils/logging";
import { navigate } from "@reach/router";

import type { AppDispatch } from "newui/store";
import store from "newui/store";

import { getCompanyProfile } from "../Company/company-api";
import { getUserProfile, getUserToken } from "./auth-api";
import type { GuestSwitch } from "./authSlice";
import {
	authenticateTwoFactor,
	logoutUser,
	setCurrentCompany,
	setCurrentUser,
	setGuestSwitching,
	setIsAuthLoading,
} from "./authSlice";
import type { User } from "./types/User.type";
import type { UserCompanyProfile } from "./types/UserCompanyProfile.type";
import type { UsersCookie } from "./types/UsersCookie.type";
import { isPAPLUsers } from "utils/helpers";
import { CompanyProfileType } from "./types/CompanyProfileType";
import { UserRoleName } from "./types/UserRole.type";
import { USER_ROLES_FOR_SELECTION } from "./constants/UserRoleConstants";
import { LabelValue } from "newui/types/LabelValue.type";

export const setSwitchFromGuest = async (
	company?: UserCompanyProfile,
	user?: User,
	url?: string
) => {
	return store.dispatch(
		setGuestSwitching({
			is_loaded: false,
			company: company,
			user: user,
			url: url,
		} as GuestSwitch)
	);
};

export const reloadUser = async () => {
	const profileResponse = await getUserProfile();

	try {
		if (!profileResponse) {
			throw new Error("Failed to retrieve User Profile.");
		}

		const userProfile = profileResponse.data;

		const companyResponse = await getCompanyProfile(userProfile.company_id);

		if (!companyResponse) {
			throw new Error("Failed to retrieve Company Details.");
		}

		const companyProfile = companyResponse.data;
	} catch (exception) {
		logErrorIfDebug(exception);
	}
};

export const refreshLoadedCompanyProfile = async (
	companyProfile: UserCompanyProfile
) => {
	store.dispatch(setCurrentCompany(companyProfile));
};

export const refreshLoadedUserProfile = async (userProfile: User) => {
	setCookie("is_admin", userProfile.is_admin.toString());
	setCookie("company_admin", userProfile.company_admin.toString() || null);

	await store.dispatch(setCurrentUser(userProfile));

	setCookie("is_admin", userProfile?.is_admin.toString());
};

export const clearGuestUser = async () => {
	localStorage.removeItem("guest_user");
	await store.dispatch(logoutUser());
};

export const makeCompanyProfileFromGuest = async (
	company: UserCompanyProfile,
	user: User
) => {
	await clearGuestUser();
	await store.dispatch(createRFQTOTAL(1));
	await refreshLoadedUserProfile(user);
	await refreshLoadedCompanyProfile(company);
};

export const processAuthenticatedUser = async (
	user: User,
	dispatch: AppDispatch,
	enforceTwoFactor: boolean = true // default true for login
): Promise<boolean> => {
	// store 2FA info in Redux
	await dispatch(
		authenticateTwoFactor({
			two_factor_enabled: user.two_factor_enabled,
			two_factor_verified: false,
		})
	);

	// Only redirect for 2FA if enforceTwoFactor is true
	if (enforceTwoFactor && user.two_factor_enabled) {
		localStorage.setItem("two_factor_enabled", "true");
		localStorage.setItem("two_factor_verified", "false");
		navigate("/two-factor");
		return false;
	}

	localStorage.setItem(
		"two_factor_enabled",
		user.two_factor_enabled.toString()
	);
	localStorage.setItem(
		"two_factor_verified",
		user.two_factor_enabled.toString()
	);

	if (enforceTwoFactor && isPAPLUsers(user)) {
		navigate("/two-factor-setup");
		return false;
	}

	// fetch full profile if missing (and not guest)
	if (!user.company && !user.is_guest) {
		const userProfileResponse = await getUserProfile();

		// TODO: need to do something here if we fail

		user = userProfileResponse?.data;
	}

	localStorage.setItem("sidebar", JSON.stringify({ sidebarcollapse: false }));

	dispatch(setCurrentUser(user));

	return true;
};

export const getOrFetchXsrfToken = async (forceRefresh: boolean = false) => {
	const xsrfToken = getCookie("XSRF-TOKEN");

	if (xsrfToken && !forceRefresh) {
		return;
	}

	await getUserToken();
};

let fetchInProgress: Promise<boolean> | null = null;

export function fetchCurrentUserOnce(dispatch: AppDispatch): Promise<boolean> {
	if (fetchInProgress) {
		return fetchInProgress;
	}

	fetchInProgress = (async () => {
		dispatch(setIsAuthLoading(true));

		try {
			await getOrFetchXsrfToken();

			const authResponse = await getUserProfile();

			if (!authResponse.data) {
				return false;
			}
			const result = await processAuthenticatedUser(
				authResponse.data,
				dispatch,
				false
			);
			return result;
		} catch (error) {
			logErrorIfDebug("fetchCurrentUserOnce ERROR", error);
		} finally {
			dispatch(setIsAuthLoading(false));
		}
	})();

	return fetchInProgress;
}

export const getPlanTypeClientOrSupplier = (
	planName?: string
): CompanyProfileType => {
	if (planName?.toLowerCase().includes("procurer")) {
		return CompanyProfileType.CLIENT;
	} else if (planName?.toLowerCase().includes("bidder")) {
		return CompanyProfileType.SUPPLIER;
	}

	return CompanyProfileType.NONE;
};

export const getUserRoleByValue = (
	roleName: UserRoleName
): LabelValue<string> => {
	return USER_ROLES_FOR_SELECTION.find((role) => role.value === roleName);
};
